import React, { useEffect, useState } from 'react';

import {
  Breakpoints,
  Checkbox,
  CTA,
  HandleSelectEvent,
  Select,
  TextAppearance,
  TextColor,
  TextCustom,
  TextInput,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';
import { useFormik } from 'formik';

import { AddressVerificationFail } from '../../../shared/components/address-verification-fail/AddressVerificationFail';
import { ErrorModal } from '../../../shared/components/errorModal/ErrorModal';
import { getFieldProps } from '../../../shared/utils/formik';
import { personalInfomtaionFormValidationSchema } from '../helpers/personal-information-form-validations';
import { stateList } from '../../../shared/utils/stateList';
import { provinceList } from '../../../shared/utils/provinceList';
import {
  StyledCheckBoxContainer,
  StyledInformationContainer,
  StyledInformationFormWrapper,
  StyledInputsContainer,
  StyledPhoneInformationContainer,
  StyledSubmitButtonContainer,
} from '../styles/personal-information-styles';
import { useAddressVerification } from '../../../shared/hooks/use-address-verification';
import { useFormErrorHandling } from '../../../shared/hooks/use-form-error-handling';
import { useGoogleMapsApi } from '../../../shared/hooks/use-google-maps-api';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { useTrackingManager } from '../../../shared/hooks/use-tracking-manager';
import {
  onVWBasicFormErrorMessageLoadEvent,
  onVWBasicFormLoadEvent,
  onVWBasicFormStepButtonClickEvent,
  onVWBasicFormStepLoadEvent,
} from '../../../shared/tagging/tagging-helpers';
import { applyTextTransformations } from '../../../shared/utils/applyTextTransformations';
import { useGoogleAutocompleteNoTranslate } from '../../../shared/hooks/use-google-autocomplete-notranslate';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useUser } from '../../../app/providers/UserContext';

interface PersonalInformationFormProps {
  onComplete: () => void;
  stepNumber?: number;
  isCaFlow?: boolean;
}

export const PersonalInformationForm = ({
  onComplete,
  stepNumber,
  isCaFlow,
}: PersonalInformationFormProps) => {
  const [tosCheck, setTosCheck] = useState<boolean>(false);
  const [trackedErrors, setTrackedErrors] = useState<Record<string, boolean>>(
    {},
  );

  const { userRegistrationSufficiencyCheck, refetchUserProfile } = useUser();
  const { authService, translationHandler, baseUrl } = useMyVwContext();
  const { 'locale-service': localeService } = useFeatureServices();

  const isCanada = localeService?.countryCode === 'CA';
  const isFrench = isCanada && localeService?.language === 'fr';
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  const {
    activeErrorModal,
    setActiveErrorModal,
    errorInformation,
    setErrorInformation,
    isSubmitting,
    setIsSubmitting,
    handleTosError,
    handleUserDataError,
    handleSubmitError,
  } = useFormErrorHandling({ authService });

  const { googleAPILoaded } = useGoogleMapsApi({
    authService,
    localeCode,
    baseUrl,
    setActiveErrorModal,
    setErrorInformation,
  });

  useGoogleAutocompleteNoTranslate(googleAPILoaded);

  const trackingManager = useTrackingManager();

  const handleFormNextTracking = () => {
    onVWBasicFormStepButtonClickEvent(
      trackingManager,
      {
        Type: 'Button',
        Name: translationHandler('registration.next.cta.label'),
      },
    );
  };

  const handleFormLoadTracking = () => {
    if (isCaFlow) {
      onVWBasicFormStepLoadEvent(trackingManager, {}, stepNumber === 1);
    } else {
      onVWBasicFormLoadEvent(trackingManager, {});
    }
  };

  const auxHandleErrorMessageLoadTracking = (errors: any) => {
    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([fieldName, errorMessage]) => {
        handleErrorMessageLoadTracking({
          code: `VALIDATION_ERROR_${fieldName.toUpperCase()}`,
          message: translationHandler(String(errorMessage)),
          referringUrl: window.location.href,
        });
      });
    }
  };

  const handleErrorMessageLoadTracking = ({
    code,
    message,
    referringUrl,
  }: {
    code: string;
    message: string;
    referringUrl: string;
  }) => {
    onVWBasicFormErrorMessageLoadEvent(
      trackingManager,
      {
        code,
        message,
        referringUrl,
        source: 'Form',
      },
    );
  };

  const formik = useFormik({
    initialValues: {
      address1: '',
      address2: '',
      city: '',
      firstName: '',
      lastName: '',
      number: '',
      state: '',
      zipCode: '',
    },
    onSubmit: async (values) => {
      setIsSubmitting(true);

      const payloadObject: Record<string, any> = {
        address1: { value: values.address1 },
        address2: { value: values.address2 },
        city: { value: values.city },
        state: { value: values.state },
        zipCode: { value: values.zipCode },
        postalCode: { value: values.zipCode },
        firstName: { value: values.firstName },
        lastName: { value: values.lastName },
        number: { value: values.number },
        country: { value: localeService.countryCode }
      };

      let addresValidation: any;

      try {
        if (!withoutAddressVerification) {
          addresValidation = isCanada
            ? await authService.addressvalidationCanada(payloadObject)
            : await authService.addressvalidation(payloadObject);

          if (
            !addresValidation ||
            (addresValidation &&
              addresValidation.data &&
              addresValidation.data.data &&
              addresValidation.data.data.verificationStatus === 'Invalid')
          )
            setAddressVerificationFail(true);
        }

        if (
          withoutAddressVerification ||
          (addresValidation &&
            addresValidation.data &&
            addresValidation.data.data &&
            addresValidation.data.data.verificationStatus !== 'Invalid')
        ) {
          try {
            !isCanada && await authService.saveUserMyVWTosResponse(localeCode);
          } catch (error) {
            handleTosError(error);
          }

          try {
            //* For local development
            // await authService.mockUpdateProfileGqlRequest(payloadObject);

            //* For integration and Prod environments
            await authService.updateProfileGqlRequest(
              payloadObject,
              localeCode,
            );

            //* Refresh user profile and registration checks
            await Promise.all([
              refetchUserProfile(),
              userRegistrationSufficiencyCheck(),
            ]);

            handleFormNextTracking();

            //* Move on to PIN creation (Step 2)
            onComplete();
          } catch (error) {
            handleUserDataError(error);
          }
        }
      } catch (error) {
        handleSubmitError(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: personalInfomtaionFormValidationSchema(isCanada),
  });

  const {
    addressInputRef,
    addressVerificationFail,
    setAddressVerificationFail,
    withoutAddressVerification,
    setWithoutAddressVerification,
    isAutocompleteInProgress,
  } = useAddressVerification({
    googleAPILoaded,
    formik,
    setActiveErrorModal,
    setErrorInformation,
  });

  const handleSelectChange: HandleSelectEvent = (
    e: React.SyntheticEvent<HTMLSelectElement>,
  ) => {
    const value = e.currentTarget.value;

    formik.setFieldValue('state', value);
    formik.setFieldTouched('state', true);

    // Force validation to run after value is set
    Promise.resolve().then(() => {
      formik.validateForm();
    });
  };

  const handleCheckBoxOnChange = (e: any) => {
    const newValue: boolean = e.target.checked;

    setTosCheck(newValue);
  };

  const isButtonDisabled: boolean =
    Object.keys(formik.errors).length > 0 ||
    !formik.dirty ||
    formik.isSubmitting ||
    isSubmitting ||
    formik.values.state === 'INVALID_STATE' ||
    (!isCanada && !tosCheck) ||
    (addressVerificationFail && !withoutAddressVerification);

  const TOSAndPrivacyCheckBox = () => {
    const tosAndPrivacyTextStart: string =
      translationHandler('registration.form.checkbox.label')
        .split(' ')
        .slice(0, 15)
        .join(' ') ||
      'By checking this box and clicking \"Next\" below, I confirm that I have reviewed the';

    const privacyString: string =
      translationHandler('registration.form.checkbox.label')
        .split(' ')
        .slice(15, 18)
        .join(' ') || 'VW Privacy Statement';

    const midString: string =
      translationHandler('registration.form.checkbox.label')
        .split(' ')
        .slice(18, 27)
        .join(' ') || 'and that I have read and agree to the';

    const tosString: string =
      translationHandler('registration.form.checkbox.label')
        .split(' ')
        .slice(27, 33)
        .join(' ') || 'myVW Terms of Service.';

    const CheckBoxLabel = () => (
      <>
        {tosAndPrivacyTextStart}{' '}
        <CTA
          aria-label="VW Privacy Statement"
          emphasis="tertiary"
          href="https://www.vw.com/en/privacy.html"
          tag="a"
          target="_blank"
        >
          {privacyString}
        </CTA>{' '}
        {midString}{' '}
        <CTA
          aria-label="myVW Terms of Service"
          emphasis="tertiary"
          href="https://www.vw.com/en/website-terms.html#myVW"
          tag="a"
          target="_blank"
        >
          {applyTextTransformations(tosString, ['myVW'])}
        </CTA>
      </>
    );

    return (
      <StyledCheckBoxContainer>
        <Checkbox
          aria-required="true"
          aria-label={translationHandler('registration.form.checkbox.label')}
          label={<CheckBoxLabel />}
          required
          onChange={handleCheckBoxOnChange}
          checked={tosCheck}
          value={String(tosCheck)}
          name="tosAndPrivacy"
        />
      </StyledCheckBoxContainer>
    );
  };

  useEffect(() => {
    handleFormLoadTracking();
  }, []);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && !isAutocompleteInProgress) {
      const touchedErrors = Object.entries(formik.errors)
        .filter(
          ([fieldName]) =>
            formik.touched[fieldName as keyof typeof formik.touched] &&
            !trackedErrors[fieldName],
        )
        .reduce(
          (acc, [fieldName, errorMessage]) => {
            acc[fieldName] = errorMessage;
            return acc;
          },
          {} as Record<string, string>,
        );

      if (Object.keys(touchedErrors).length > 0) {
        auxHandleErrorMessageLoadTracking(touchedErrors);

        setTrackedErrors((prev) => ({
          ...prev,
          ...Object.keys(touchedErrors).reduce(
            (acc, key) => {
              acc[key] = true;
              return acc;
            },
            {} as Record<string, boolean>,
          ),
        }));
      }
    }
  }, [formik.errors, formik.touched, isAutocompleteInProgress]);

  useEffect(() => {
    if (!isAutocompleteInProgress && formik.dirty) {
      setTrackedErrors((prev) => {
        const newTrackedErrors = { ...prev };
        if (!formik.errors.address1) delete newTrackedErrors.address1;
        if (!formik.errors.city) delete newTrackedErrors.city;
        if (!formik.errors.state) delete newTrackedErrors.state;
        if (!formik.errors.zipCode) delete newTrackedErrors.zipCode;
        return newTrackedErrors;
      });
    }
  }, [isAutocompleteInProgress]);

  useEffect(() => {
    Object.keys(trackedErrors).forEach((fieldName) => {
      if (!formik.errors[fieldName as keyof typeof formik.errors]) {
        setTrackedErrors((prev) => {
          const newTrackedErrors = { ...prev };
          delete newTrackedErrors[fieldName];
          return newTrackedErrors;
        });
      }
    });
  }, [formik.errors]);

  return (
    <>
      {setActiveErrorModal && (
        <ErrorModal
          contentGap={35}
          isActive={activeErrorModal}
          setErrorModal={setActiveErrorModal}
          title={
            <TextCustom
              appearance={TextAppearance.headline200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  color: TextColor.primary,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight18,
                },
              }}
            >
              {translationHandler('error.modal.title')}
            </TextCustom>
          }
          upperContent={
            <TextCustom
              appearance={TextAppearance.headline200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize14,
                  color: TextColor.primary,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight16,
                },
              }}
            >
              {translationHandler(errorInformation.errorMessage)}
            </TextCustom>
          }
          lowerContent={
            <TextCustom
              appearance={TextAppearance.headline200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize14,
                  color: TextColor.primary,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight16,
                },
              }}
            >
              {translationHandler('error.contact.info.message')}
            </TextCustom>
          }
        />
      )}
      <StyledInformationFormWrapper>
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsContainer>
            <TextInput
              {...getFieldProps(formik, 'firstName', translationHandler)}
              aria-label={translationHandler(
                'registration.form.first.name.label',
              )}
              aria-required="true"
              label={translationHandler('registration.form.first.name.label')}
              required
            />

            <TextInput
              {...getFieldProps(formik, 'lastName', translationHandler)}
              aria-label={translationHandler(
                'registration.form.last.name.label',
              )}
              aria-required="true"
              label={translationHandler('registration.form.last.name.label')}
              required
            />

            <TextInput
              {...getFieldProps(formik, 'address1', translationHandler)}
              aria-label={translationHandler('registration.form.address.label')}
              aria-required="true"
              label={translationHandler('registration.form.address.label')}
              required
              ref={addressInputRef}
            />

            <TextInput
              {...getFieldProps(formik, 'address2', translationHandler)}
              aria-label={translationHandler(
                'registration.form.address.line.two.label',
              )}
              label={translationHandler(
                'registration.form.address.line.two.label',
              )}
            />

            <TextInput
              {...getFieldProps(formik, 'city', translationHandler)}
              aria-label={translationHandler('registration.form.city.label')}
              aria-required="true"
              label={translationHandler('registration.form.city.label')}
              required
            />

            <Select
              appearance={
                formik.touched.state &&
                formik.errors.state &&
                formik.values.state === ''
                  ? ('error' as any)
                  : undefined
              }
              aria-label="Select city component"
              aria-required="true"
              defaultValue=""
              isFloating
              message={
                formik.touched.state &&
                formik.errors.state &&
                formik.values.state === ''
                  ? translationHandler(formik.errors.state)
                  : undefined
              }
              onBlur={() => {
                formik.setFieldTouched('state', true);
              }}
              onChange={handleSelectChange}
              required
              value={formik.values.state}
              name="state"
            >
              <option value="">
                {translationHandler('registration.form.select.state.label')}
              </option>
              {isCanada
                ? provinceList.map((item, index: number) => (
                    <option key={`Province-list-option-${index + 1}`} value={item.code}>
                      {applyTextTransformations(isFrench ? item.frenchName : item.name, [isFrench ? item.frenchName : item.name])}
                    </option>
                  ))
                : stateList.map((item: any, index: number) => (
                    <option key={`State-list-option-${index + 1}`} value={item.code}>
                      {applyTextTransformations(item.name, [item.name])}
                    </option>
                  ))}
            </Select>

            <TextInput
              {...getFieldProps(formik, 'zipCode', translationHandler)}
              aria-label={translationHandler(
                'registration.form.zip.code.label',
              )}
              aria-required="true"
              label={translationHandler('registration.form.zip.code.label')}
              required
            />

            <TextInput
              {...getFieldProps(formik, 'number', translationHandler)}
              aria-label={translationHandler(
                'registration.form.mobile.number.label',
              )}
              aria-required="true"
              label={translationHandler(
                'registration.form.mobile.number.label',
              )}
              maxLength={10}
              required
            />
          </StyledInputsContainer>
          <StyledInformationContainer>
            <StyledPhoneInformationContainer>
              <TextCustom
                appearance={TextAppearance.copy100}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize12,
                    color: TextColor.primary,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight20,
                  },
                }}
              >
                {applyTextTransformations(
                  translationHandler('registration.form.phone.information'),
                  ['myVW', 'PIN'],
                )}
              </TextCustom>
            </StyledPhoneInformationContainer>
            {addressVerificationFail && (
              <AddressVerificationFail
                withoutAddressVerification={withoutAddressVerification}
                setWithoutAddressVerification={setWithoutAddressVerification}
                translationHandler={translationHandler}
              />
            )}
          </StyledInformationContainer>
          {!isCanada && <TOSAndPrivacyCheckBox />}
          <StyledSubmitButtonContainer>
            <CTA
              aria-label="Submit button"
              disabled={isButtonDisabled}
              emphasis="primary"
              stretchContent
              tag="button"
              type="submit"
            >
              {translationHandler('registration.next.cta.label')}
            </CTA>
          </StyledSubmitButtonContainer>
        </form>
      </StyledInformationFormWrapper>
    </>
  );
};
